import { t } from 'i18next';

import { FullProjectIssue, User, isIssueOpen, actions } from 'entities';

import { Table, Button, Card, Row } from 'components/Blocks';
import { UserAvatar } from 'components/UserAvatar';
import { UserMinusIcon } from 'components/Icons';

import { AddUser } from './AddUser';

import { useUser } from 'hooks/user';
import { useConfirmation } from 'providers/Confirmation';

import { formatDate } from 'utils/date';

import { BaseProps } from './types';

export function IssueUsers({ issue, updateIssue }: BaseProps) {
  const currentUser = useUser();
  const { requestConfirmation } = useConfirmation();

  const allUsers = issue
    ? [
        { user: issue.owner, addedAt: issue.createdAt, type: 'CREATOR', id: issue.id },
        ...issue.salesInvolved,
        ...issue.executorsInvolved,
      ]
    : [];

  const canModify = isIssueOpen(issue);

  const removeUserFromIssue = actions.projects.removeUserFromIssue.use({
    onSuccess: (_, { type, user: userId }) => {
      const key = (
        type === 'SALES' ? 'salesInvolved' : 'executorsInvolved'
      ) as keyof FullProjectIssue;

      updateIssue({
        ...issue,

        [key]: (issue?.[key] as FullProjectIssue['salesInvolved']).filter(
          (entry) => entry.user.id !== userId,
        ),
      } as FullProjectIssue);
    },
  });

  const removeUser = (user: User, type: string): void => {
    requestConfirmation({
      title: t('remove-user', { user: user.name }) as string,

      text: t('user-will-be-removed-from-issue'),

      onConfirm: async () => {
        await removeUserFromIssue.execute({
          issue: issue!.id,
          project: issue!.project,
          user: user.id,
          type,
        });
      },
    });
  };

  return (
    <Card>
      <Table
        items={allUsers}
        uniqueKey="id"
        headerClassName="pb-6"
        columns={[
          {
            label: (
              <Row gap="2">
                {t('user')}

                {currentUser.id === issue?.owner.id && canModify && (
                  <AddUser issue={issue} updateIssue={updateIssue} />
                )}
              </Row>
            ),

            render: ({ user }) => <UserAvatar user={user} name />,
          },

          {
            label: t('added-at'),

            render: ({ addedAt }) => formatDate(addedAt),

            className: 'w-28',
          },
          {
            label: t('type'),
            property: 'type',
          },

          {
            render: ({ user, type }) => {
              if (!canModify || user.id === issue?.owner.id || currentUser.id !== issue?.owner.id)
                return null;

              return (
                <Button
                  variant="ghost"
                  onClick={() => removeUser(user, type)}
                  tooltipContent={t('remove')}
                  className="ml-auto flex"
                >
                  <UserMinusIcon />
                </Button>
              );
            },
          },
        ]}
      />
    </Card>
  );
}
